import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FaInfoCircle,
  FaArrowLeft,
  FaMoneyBillWave,
  FaUniversity,
  FaCreditCard,
  FaUser,
} from "react-icons/fa";
import CustomerLayout from "../components/layout/CustomerLayout";

export default function WalletWithdrawal() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const REFUND_FEE = 200;
  const MIN_AMOUNT = 1000;
  const walletBalance = parseFloat(user?.wallet_balance || 0);

  const [form, setForm] = useState({
    payer_first_name: user?.first_name || "",
    payer_last_name: user?.last_name || "",
    payer_email: user?.email || "",
    payer_phone_number: user?.phone_number || "",
    recipient_first_name: user?.first_name || "",
    recipient_last_name: user?.last_name || "",
    recipient_bank_name: "",
    recipient_account_number: "",
    recipient_account_name: `${user?.first_name || ""} ${user?.last_name || ""}`.trim(),
    amount: "",
    transaction_description: "",
  });
  const [error, setError] = useState("");

  const amt = parseFloat(form.amount) || 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "recipient_account_number" && value && !/^\d*$/.test(value)) return;
    setForm((p) => ({ ...p, [name]: value }));
    setError("");
  };

  const handleProceed = () => {
    if (!form.recipient_bank_name || !form.recipient_account_number || !form.recipient_account_name) {
      setError("Please fill in all bank details.");
      return;
    }
    if (amt < MIN_AMOUNT) {
      setError(`Minimum refund is ₦${MIN_AMOUNT.toLocaleString()}.`);
      return;
    }
    navigate("/wallet/withdraw/confirm", { state: { withdrawalDetails: form, walletBalance } });
  };

  return (
    <CustomerLayout>
      <div className="max-w-2xl mx-auto space-y-5">
        <button
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#0d3d2e] border border-green-200 rounded-xl px-4 py-2 hover:bg-green-50 transition"
        >
          <FaArrowLeft className="w-3 h-3" /> Back to Dashboard
        </button>

        <div>
          <h1 className="text-3xl font-black text-gray-900 mb-1.5">Request a Refund</h1>
          <p className="text-gray-500">Withdraw funds from your UCH wallet to your bank account.</p>
        </div>

        {/* Balance */}
        <div className="bg-gradient-to-br from-[#0d3d2e] to-[#0f4a38] rounded-2xl p-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative z-10">
            <p className="text-green-300 text-xs font-semibold uppercase tracking-widest mb-1.5">
              Available Balance
            </p>
            <p className="text-4xl font-black mb-2">
              ₦{walletBalance.toLocaleString("en-NG", { minimumFractionDigits: 2 })}
            </p>
            <p className="text-green-200 text-xs">
              Refund Fee: ₦{REFUND_FEE} &nbsp;·&nbsp; Minimum: ₦{MIN_AMOUNT.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3.5 text-sm text-blue-800">
          <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center shrink-0 mt-0.5">
            <FaInfoCircle className="w-3 h-3" />
          </span>
          <p className="leading-relaxed">
            A processing fee of <strong>₦{REFUND_FEE}</strong> will be deducted. Minimum
            refund amount is <strong>₦{MIN_AMOUNT.toLocaleString()}</strong>. Refunds are
            subject to Super Admin approval. Refund typically takes{" "}
            <strong>3 business days</strong> after approval.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 space-y-5">
          <h3 className="font-bold text-gray-900 border-b border-gray-50 pb-4">Refund Details</h3>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Refund Amount (₦)<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <FaMoneyBillWave className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="number"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                placeholder={`Min ₦${MIN_AMOUNT.toLocaleString()}`}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Bank Name<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <FaUniversity className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="text"
                name="recipient_bank_name"
                value={form.recipient_bank_name}
                onChange={handleChange}
                placeholder="e.g. First Bank, GTBank"
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Account Number<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <FaCreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="text"
                inputMode="numeric"
                name="recipient_account_number"
                value={form.recipient_account_number}
                onChange={handleChange}
                placeholder="10-digit account number"
                maxLength={10}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Account Name<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="text"
                name="recipient_account_name"
                value={form.recipient_account_name}
                onChange={handleChange}
                placeholder="As on bank records"
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Reason for Refund <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              name="transaction_description"
              value={form.transaction_description}
              onChange={handleChange}
              placeholder="Briefly describe the reason..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition resize-none"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleProceed}
            disabled={!amt || amt < MIN_AMOUNT}
            className="w-full bg-[#0d3d2e] hover:bg-[#0a3226] text-white font-semibold py-3.5 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Proceed to Confirm
          </button>
        </div>
      </div>
    </CustomerLayout>
  );
}
